import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * A custom element that applies a discount to the cart.
 *
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError - The error element.
 * @property {HTMLElement} cartDiscountErrorSupplementary - The supplementary error element.
 * @property {HTMLElement} cartDiscountErrorDiscountCode - The discount code error element.
 * @property {HTMLElement} cartDiscountErrorAlreadyApplied - The already applied discount error element.
 * @property {HTMLElement} cartDiscountErrorShipping - The shipping error element.
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = [
    'cartDiscountError',
    'cartDiscountErrorSupplementary',
    'cartDiscountErrorDiscountCode',
    'cartDiscountErrorAlreadyApplied',
    'cartDiscountErrorShipping',
  ];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  #setLoadingState(isLoading) {
    this.dataset.loading = String(isLoading);

    const discountInput = this.querySelector('input[name="discount"]');
    if (discountInput instanceof HTMLInputElement) {
      discountInput.readOnly = isLoading;
      discountInput.setAttribute('aria-busy', String(isLoading));
    }

    const submitButton = this.querySelector('.cart-discount-sheet__apply');
    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = isLoading;
      submitButton.setAttribute('aria-busy', String(isLoading));
    }

    for (const radio of this.querySelectorAll('.cart-discount-sheet__list-card-radio')) {
      if (radio instanceof HTMLInputElement) {
        radio.disabled = isLoading;
      }
    }
  }

  /**
   * Returns the nearest cart container used for discount loading state.
   * @returns {HTMLElement | null}
   */
  #discountContainer() {
    const container = this.closest('.cart-summary-panel, .mini-cart');
    return container instanceof HTMLElement ? container : null;
  }

  /**
   * Toggles loading state for a specific button or button group.
   * @param {HTMLElement | null} target
   * @param {boolean} isLoading
   */
  #setButtonLoadingState(target, isLoading) {
    if (!(target instanceof HTMLElement)) return;
    target.toggleAttribute('data-loading', isLoading);
    target.classList.toggle('is-loading', isLoading);
  }

  /**
   * Hides all discount error containers and messages.
   */
  #clearDiscountErrors() {
    const {
      cartDiscountError,
      cartDiscountErrorSupplementary,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorAlreadyApplied,
      cartDiscountErrorShipping,
    } = this.refs;

    cartDiscountError.classList.add('hidden');
    cartDiscountError.dataset.errorState = '';
    cartDiscountErrorSupplementary.classList.add('hidden');
    cartDiscountErrorSupplementary.dataset.errorState = '';
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorAlreadyApplied.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');
  }

  /**
   * Handles updates to the cart note.
   * @param {SubmitEvent} event - The submit event on our form.
   */
  applyDiscount = async (event) => {
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorAlreadyApplied,
      cartDiscountErrorShipping,
    } = this.refs;

    event.preventDefault();
    event.stopPropagation();

    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const discountCode = form.querySelector('input[name="discount"]');
    if (!(discountCode instanceof HTMLInputElement) || typeof this.dataset.sectionId !== 'string') return;

    const discountCodeValue = discountCode.value.trim();
    if (!discountCodeValue) return;

    try {
      const existingDiscounts = this.#existingDiscounts();
      if (existingDiscounts.includes(discountCodeValue)) {
        this.dataset.inputState = 'invalid';
        this.#handleDiscountError('already_applied', discountCode);
        return;
      }

      this.#clearDiscountErrors();
      discountCode.removeAttribute('aria-invalid');
      this.dataset.inputState = '';
      this.#setLoadingState(true);
      const abortController = this.#createAbortController();

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [...existingDiscounts, discountCodeValue].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      if (
        data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
          return discount.code === discountCodeValue && discount.applicable === false;
        })
      ) {
        this.dataset.inputState = 'invalid';
        this.#handleDiscountError('discount_code', discountCode);
        return;
      }

      const newHtml = data.sections[this.dataset.sectionId];
      const parsedHtml = new DOMParser().parseFromString(newHtml, 'text/html');
      const section = parsedHtml.getElementById(`shopify-section-${this.dataset.sectionId}`);
      const discountCodes = section?.querySelectorAll('.cart-discount__codes .cart-discount__pill') || [];
      if (section) {
        const codes = Array.from(discountCodes)
          .map((element) => (element instanceof HTMLLIElement ? element.dataset.discountCode : null))
          .filter(Boolean);
        // Before morphing, we need to check if the shipping discount is applicable in the UI
        // we check the liquid logic compared to the cart payload to assess whether we leveraged
        // a valid shipping discount code.
        if (
          codes.length === existingDiscounts.length &&
          codes.every((/** @type {string} */ code) => existingDiscounts.includes(code)) &&
          data.discount_codes.find((/** @type {{ code: string; applicable: boolean; }} */ discount) => {
            return discount.code === discountCodeValue && discount.applicable === true;
          })
        ) {
          this.dataset.inputState = 'invalid';
          this.#handleDiscountError('shipping');
          return;
        }
      }

      this.dataset.inputState = 'success';
      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(
        this.dataset.sectionId,
        this.#preserveActiveTabOnMarkup(
          this.#preserveInputValueOnMarkup(this.#preserveInputStateOnMarkup(newHtml, 'success'), discountCodeValue)
        )
      );

      const discountSheet = this.closest('cart-discount-sheet');
      if (discountSheet instanceof HTMLElement && discountSheet.classList.contains('cart-discount-sheet--popup')) {
        queueMicrotask(() => {
          discountSheet.closeDialog?.();
        });
      }
    } catch (error) {
    } finally {
      this.#setLoadingState(false);
      this.#activeFetch = null;
      cartPerformance.measureFromEvent('discount-update:user-action', event);
    }
  };

  /**
   * Handles removing a discount from the cart.
   * @param {MouseEvent | KeyboardEvent} event - The mouse or keyboard event in our pill.
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent && event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest('.cart-discount__pill');
    if (!(pill instanceof HTMLLIElement)) return;

    const discountCode = pill.dataset.discountCode;
    if (!discountCode) return;

    const existingDiscounts = this.#existingDiscounts();
    const index = existingDiscounts.indexOf(discountCode);
    if (index === -1) return;

    existingDiscounts.splice(index, 1);

    const removeButton = pill.querySelector('.cart-discount__pill-remove');
    const abortController = this.#createAbortController();
    const container = this.#discountContainer();
    this.#setButtonLoadingState(removeButton, true);
    this.#setLoadingState(true);
    container?.toggleAttribute('data-discount-loading', true);

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: existingDiscounts.join(','), sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, this.#preserveActiveTabOnMarkup(data.sections[this.dataset.sectionId]));
    } catch (error) {
    } finally {
      this.#setButtonLoadingState(removeButton, false);
      this.#setLoadingState(false);
      container?.toggleAttribute('data-discount-loading', false);
      this.#activeFetch = null;
    }
  };

  /**
   * Removes all applied discount codes from the cart.
   */
  removeAppliedDiscounts = async () => {
    if (typeof this.dataset.sectionId !== 'string') return;

    const existingDiscounts = this.#existingDiscounts();
    if (existingDiscounts.length === 0) return;

    this.#setLoadingState(true);
    const container = this.#discountContainer();
    const removeButtons = container?.querySelectorAll('.cart-summary__breakdown-remove, .mini-cart__breakdown-remove') || [];
    for (const button of removeButtons) {
      if (button instanceof HTMLElement) {
        button.toggleAttribute('data-loading', true);
        button.classList.toggle('is-loading', true);
      }
    }
    container?.toggleAttribute('data-discount-loading', true);
    const abortController = this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ discount: '', sections: [this.dataset.sectionId] }),
      });

      const response = await fetch(Theme.routes.cart_update_url, {
        ...config,
        signal: abortController.signal,
      });

      const data = await response.json();

      document.dispatchEvent(new DiscountUpdateEvent(data, this.id));
      morphSection(this.dataset.sectionId, this.#preserveActiveTabOnMarkup(data.sections[this.dataset.sectionId]));
    } catch (error) {
    } finally {
      this.#setLoadingState(false);
      for (const button of removeButtons) {
        if (button instanceof HTMLElement) {
          button.toggleAttribute('data-loading', false);
          button.classList.toggle('is-loading', false);
        }
      }
      container?.toggleAttribute('data-discount-loading', false);
      this.#activeFetch = null;
    }
  };

  /**
   * Dismisses the visible discount error alert.
   * @param {MouseEvent} event
   */
  dismissError = (event) => {
    event.preventDefault();
    event.stopPropagation();

    this.#clearDiscountErrors();

    const discountInput = this.querySelector('input[name="discount"]');
    if (discountInput instanceof HTMLInputElement) {
      discountInput.removeAttribute('aria-invalid');
    }
    this.dataset.inputState = '';
  };

  /**
   * Handles the discount error.
   *
   * @param {'discount_code' | 'already_applied' | 'shipping'} type - The type of discount error.
   * @param {HTMLInputElement | null} [input] - The discount input to flag as invalid.
   */
  #handleDiscountError(type, input = null) {
    const {
      cartDiscountError,
      cartDiscountErrorSupplementary,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorAlreadyApplied,
      cartDiscountErrorShipping,
    } = this.refs;

    this.#clearDiscountErrors();
    cartDiscountErrorDiscountCode.classList.add('hidden');
    cartDiscountErrorAlreadyApplied.classList.add('hidden');
    cartDiscountErrorShipping.classList.add('hidden');
    const target = type === 'discount_code'
      ? cartDiscountErrorDiscountCode
      : type === 'already_applied'
        ? cartDiscountErrorAlreadyApplied
        : cartDiscountErrorShipping;

    if (type === 'discount_code') {
      cartDiscountError.dataset.errorState = type;
      cartDiscountError.classList.remove('hidden');
    } else {
      cartDiscountErrorSupplementary.dataset.errorState = type;
      cartDiscountErrorSupplementary.classList.remove('hidden');
    }

    target.classList.remove('hidden');

    if (input instanceof HTMLInputElement) {
      input.setAttribute('aria-invalid', 'true');
    }
  }

  /**
   * Handles typing in the discount input to clear feedback state.
   * @param {InputEvent} event
   */
  handleInput = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.name !== 'discount') return;

    this.dataset.inputState = '';
    target.removeAttribute('aria-invalid');

    this.#clearDiscountErrors();
  };

  /**
   * Returns an array of existing discount codes.
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];
    const discountPills = this.querySelectorAll('.cart-discount__codes .cart-discount__pill');
    for (const pill of discountPills) {
      if (pill instanceof HTMLLIElement && typeof pill.dataset.discountCode === 'string') {
        discountCodes.push(pill.dataset.discountCode);
      }
    }

    return discountCodes;
  }

  /**
   * Preserves the active discount tab when the section is morphed.
   * @param {string} markup
   * @returns {string}
   */
  #preserveActiveTabOnMarkup(markup) {
    const activeTab = this.closest('cart-discount-sheet')?.querySelector('[data-discount-tab][aria-selected="true"]');
    const tabId = activeTab instanceof HTMLElement ? activeTab.dataset.discountTab : '';

    if (!tabId) return markup;

    return markup.replace(
      /(<cart-discount-sheet\b[^>]*)(>)/,
      (match, start, end) => {
        if (/data-active-tab=/.test(start)) return match;
        return `${start} data-active-tab="${tabId}"${end}`;
      }
    );
  }

  /**
   * Preserves the input feedback state when the section is morphed.
   * @param {string} markup
   * @param {'success' | 'invalid' | ''} state
   * @returns {string}
   */
  #preserveInputStateOnMarkup(markup, state) {
    return markup.replace(
      /(<cart-discount-component\b[^>]*)(>)/,
      (match, start, end) => {
        if (/data-input-state=/.test(start)) {
          return start.replace(/data-input-state="[^"]*"/, `data-input-state="${state}"`) + end;
        }

        return `${start} data-input-state="${state}"${end}`;
      }
    );
  }

  /**
   * Preserves the discount input value when the section is morphed.
   * @param {string} markup
   * @param {string} value
   * @returns {string}
   */
  #preserveInputValueOnMarkup(markup, value) {
    return markup.replace(
      /(<input\b[^>]*id="cart-discount-sheet-input"[^>]*)(>)/,
      (match, start, end) => {
        if (/value="/.test(start)) {
          return start.replace(/value="[^"]*"/, `value="${value.replace(/"/g, '&quot;')}"`) + end;
        }

        return `${start} value="${value.replace(/"/g, '&quot;')}"${end}`;
      }
    );
  }
}

if (!customElements.get('cart-discount-component')) {
  customElements.define('cart-discount-component', CartDiscount);
}
